import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  Image,
  ScrollView,
  ActivityIndicator,
  StyleSheet,
  TouchableOpacity,
  Platform,
} from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useSelector } from 'react-redux';
import * as ImagePicker from 'expo-image-picker';
import { doc, setDoc } from 'firebase/firestore';
import Feather from '@expo/vector-icons/Feather';
import { Paprika, PaprikaTypography, PaprikaRadius } from '../../src/styles/theme';
import { RootState } from '../../src/redux/store';
import aiService, { isQuotaExceededError, isNetworkFailure, isAuthRequiredError, type RecipePhotoStage } from '../../src/services/aiService';
import { authHrefReturningTo } from '../../src/utils/postAuthReturn';
import { ExtractedPhotoRecipe } from '../../src/types/photoImport';
import PhotoRecipeReviewScreen from '../../src/components/recipes/PhotoRecipeReviewScreen';
import { firestore, isFirebaseEnabled } from '../../src/services/firebaseConfig';
import { resolveDataScope, buildScopedPath } from '../../src/services/householdPaths';
import { getActiveHouseholdId } from '../../src/services/householdContext';
import unifiedRecipeService from '../../src/services/unifiedRecipeService';
import { trackEvent } from '../../src/services/analyticsService';
import { buildPhotoRecipeDoc } from '../../src/services/photoImport/buildPhotoRecipeDoc';
import { requestTranslationAtWrite } from '../../src/services/recipeTranslationRequest';
import { classifyOnImport, reportClassification } from '../../src/services/importClassification';
import { uploadRecipeImage } from '../../src/services/photoImport/uploadRecipeImage';
import { useTranslation } from 'react-i18next';
import { RequireAccountToScan } from '../../src/components/auth/ScanSignUp';

const normalizeTitle = (title: string): string =>
  title
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]/g, '')
    .trim();

// A guest arriving here would only ever get a 401 (#775).
export default function PhotoRecipeReviewGate() {
  return (
    <RequireAccountToScan kind="recipe" source="recipe_photo" returnTo="/recipes">
      <PhotoRecipeReviewRoute />
    </RequireAccountToScan>
  );
}

function PhotoRecipeReviewRoute() {
  const { t } = useTranslation();
  const router = useRouter();
  const params = useLocalSearchParams();
  const { user } = useSelector((state: RootState) => state.auth);

  const initialUri = params.imageUri as string;
  const source = (params.source as string) || 'library';

  const [imageUris, setImageUris] = useState<string[]>([initialUri].filter(Boolean));
  const [isGathering, setIsGathering] = useState(true);
  const [recipe, setRecipe] = useState<ExtractedPhotoRecipe | null>(null);
  const [confidence, setConfidence] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [progressStage, setProgressStage] = useState<RecipePhotoStage>('preparing');
  const [isSaving, setIsSaving] = useState(false);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);
  // The server saw no session: a way to sign back in replaces Try again.
  const [signedOut, setSignedOut] = useState(false);

  useEffect(() => {
    if (!initialUri) router.back();
  }, [initialUri]);

  const analyzePhoto = async (uris: string[]) => {
    setIsGathering(false);
    setIsLoading(true);
    setAnalyzeError(null);
    setSignedOut(false);
    try {
      const result = await aiService.analyzeRecipePhoto(uris, setProgressStage);
      setRecipe(result.recipe);
      setConfidence(result.confidence);
    } catch (err: any) {
      console.error(
        'Photo recipe analysis failed:',
        err instanceof Error ? err.message : String(err),
      );
      // Same reason as the receipt scanner: the server writes its refusals in
      // English for tooling, never for a cook (#692).
      setSignedOut(isAuthRequiredError(err));
      setAnalyzeError(
        isAuthRequiredError(err)
          ? t('scanGate.signedOut')
          : isQuotaExceededError(err)
          ? t('addRecipe.errors.quotaExceeded')
          : isNetworkFailure(err)
            ? t('errors.networkError')
            : err?.message || t('errors.imageProcessingFailed'),
      );
      setRecipe(null);
      setConfidence(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddPage = useCallback(async () => {
    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        quality: 1,
      });
      if (!result.canceled && result.assets[0]?.uri) {
        setImageUris(prev => [...prev, result.assets[0].uri]);
      }
    } catch {
      // unreported: the image picker rejects when the cook dismisses it.
    }
  }, []);

  const handleScanAll = useCallback(() => {
    analyzePhoto(imageUris);
  }, [imageUris]);

  const handleSave = useCallback(async (openEdit = false) => {
    if (!recipe || !user?.uid) return;

    setIsSaving(true);
    try {
      if (!isFirebaseEnabled() || !firestore) throw new Error('Firebase not enabled');

      const timestamp = Date.now();
      const random = Math.random().toString(36).substring(2, 8);
      const titleSlug = normalizeTitle(recipe.title).slice(0, 20);
      const documentId = `photo-${titleSlug}-${timestamp}-${random}`;

      const scope = resolveDataScope(user.uid, getActiveHouseholdId());
      if (!scope) throw new Error('Cannot resolve data scope');
      const [root, scopeId, ...rest] = buildScopedPath(scope, 'savedRecipes', documentId);
      const recipeRef = doc(firestore, root, scopeId, ...rest);

      // Persist the captured photo (page 1) to Storage so the recipe has a real
      // image instead of a placeholder. Falls back to no image on any failure.
      const imageUrl = await uploadRecipeImage(imageUris[0], user.uid, documentId);

      const photoDoc = buildPhotoRecipeDoc(recipe, {
        sourceName: t('photoImport.sourceName'),
        documentId,
        userId: user.uid,
        confidence,
        imageUrl: imageUrl ?? '',
      });

      // The vision model usually names a dish type, but it only counts when it
      // is canonical — otherwise the builder leaves it blank. Fall back to the
      // title heuristics rather than letting it land unclassified.
      if (photoDoc.dishTypes.length === 0) {
        photoDoc.dishTypes = classifyOnImport({
          importPath: 'photo-import',
          title: recipe.title,
        });
        photoDoc.recipeCategory = photoDoc.dishTypes[0] || '';
      } else {
        reportClassification({
          importPath: 'photo-import',
          dishType: photoDoc.dishTypes[0],
          source: 'vision-model',
          confidence: null,
          hadRawCategory: false,
        });
      }

      await setDoc(recipeRef, photoDoc);
      requestTranslationAtWrite(documentId, 'photo-import');

      trackEvent('recipe_imported', {
        source: 'photo',
        photoSource: source,
        confidence,
        ingredientCount: recipe.ingredients.length,
        instructionCount: recipe.instructions.length,
      });

      // Measures the classification feedback loop: the ratio of
      // recipe_classification_corrected to _predicted should trend to zero.
      if (recipe.dishType) {
        trackEvent('recipe_classification_predicted', {
          recipe_id: documentId,
          dish_type: recipe.dishType,
          source: 'photo-import',
        });
      }

      unifiedRecipeService.invalidateCache();

      router.replace({
        pathname: '/(app)/recipe-detail/[id]',
        params: {
          id: documentId,
          from: 'add',
          ...(openEdit ? { edit: 'true' } : {}),
        },
      });
    } catch (err: any) {
      console.error('Failed to save photo recipe:', err);
      setIsSaving(false);
    }
  }, [recipe, user, confidence, source, router]);

  const handleEdit = useCallback(() => {
    handleSave(true);
  }, [handleSave]);

  const handleRetake = useCallback(async () => {
    try {
      const result = await ImagePicker.launchCameraAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        quality: 1,
      });
      if (!result.canceled && result.assets[0]?.uri) {
        setImageUris([result.assets[0].uri]);
        setIsGathering(true);
        setRecipe(null);
        setConfidence(0);
        setAnalyzeError(null);
      } else {
        router.back();
      }
    } catch {
      // unreported: the image picker rejects when the cook dismisses it.
      router.back();
    }
  }, [router]);

  const handleChooseAnother = useCallback(async () => {
    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        quality: 1,
      });
      if (!result.canceled && result.assets[0]?.uri) {
        setImageUris([result.assets[0].uri]);
        setIsGathering(true);
        setRecipe(null);
        setConfidence(0);
        setAnalyzeError(null);
      } else {
        router.back();
      }
    } catch {
      // unreported: the image picker rejects when the cook dismisses it.
      router.back();
    }
  }, [router]);

  if (isGathering) {
    return (
      <View style={styles.screen}>
        <View style={styles.header}>
          <TouchableOpacity onPress={() => router.back()} style={styles.headerBack}>
            <Feather name="arrow-left" size={22} color={Paprika.textPrimary} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>{t('photoImport.importRecipe')}</Text>
          <View style={styles.headerBack} />
        </View>
        <GatheringScreen
          imageUris={imageUris}
          onAddPage={handleAddPage}
          onScan={handleScanAll}
        />
      </View>
    );
  }

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <TouchableOpacity style={styles.closeButton} onPress={() => router.back()}>
          <Feather name="x" size={24} color={Paprika.textSecondary} />
        </TouchableOpacity>
        <ActivityIndicator size="large" color={Paprika.primary} />
        <Text style={styles.loadingText}>
          {t(progressStage === 'preparing' ? 'photoImport.preparingPhoto' : 'photoImport.analyzingPhoto')}
        </Text>
        <Text style={styles.loadingHint}>{t('photoImport.readingRecipe')}</Text>
      </View>
    );
  }

  if (analyzeError && !recipe) {
    return (
      <View style={styles.loadingContainer}>
        <TouchableOpacity style={styles.closeButton} onPress={() => router.back()}>
          <Feather name="x" size={24} color={Paprika.textSecondary} />
        </TouchableOpacity>
        <View style={styles.errorIcon}>
          <Feather name="alert-circle" size={32} color={Paprika.primary} />
        </View>
        <Text style={styles.errorTitle}>{t('common.somethingWentWrong')}</Text>
        <Text style={styles.errorBody}>{analyzeError}</Text>
        {signedOut ? (
          <TouchableOpacity
            style={styles.retryButton}
            onPress={() => router.push(authHrefReturningTo('/recipes') as any)}
          >
            <Text style={styles.retryButtonText}>{t('scanGate.signInAgain')}</Text>
          </TouchableOpacity>
        ) : (
          <TouchableOpacity style={styles.retryButton} onPress={() => analyzePhoto(imageUris)}>
            <Text style={styles.retryButtonText}>{t('addRecipe.tryAgain')}</Text>
          </TouchableOpacity>
        )}
      </View>
    );
  }

  return (
    <View style={styles.screen}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => router.back()} style={styles.headerBack}>
          <Feather name="arrow-left" size={22} color={Paprika.textPrimary} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{t('photoImport.reviewRecipe')}</Text>
        <View style={styles.headerBack} />
      </View>
      <PhotoRecipeReviewScreen
        imageUri={imageUris[0]}
        recipe={recipe}
        confidence={confidence}
        isSaving={isSaving}
        onSave={() => handleSave(false)}
        onEdit={handleEdit}
        onRetake={handleRetake}
        onChooseAnother={handleChooseAnother}
      />
    </View>
  );
}

function GatheringScreen({
  imageUris,
  onAddPage,
  onScan,
}: {
  imageUris: string[];
  onAddPage: () => void;
  onScan: () => void;
}) {
  const { t } = useTranslation();
  return (
    <View style={gatherStyles.container}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={gatherStyles.strip}
      >
        {imageUris.map((uri, i) => (
          <View key={i} style={gatherStyles.thumbWrap}>
            <Image source={{ uri }} style={gatherStyles.thumb} resizeMode="cover" />
            <Text style={gatherStyles.pageLabel}>{t('photoImport.pageNumber', { number: i + 1 })}</Text>
          </View>
        ))}
      </ScrollView>
      <Text style={gatherStyles.hint}>
        {imageUris.length === 1
          ? t('photoImport.addMorePagesHint')
          : t('photoImport.pagesReady', { count: imageUris.length })}
      </Text>
      <View style={gatherStyles.actions}>
        <TouchableOpacity style={gatherStyles.addButton} onPress={onAddPage}>
          <Feather name="plus" size={16} color={Paprika.primary} />
          <Text style={gatherStyles.addButtonText}>{t('photoImport.addAnotherPage')}</Text>
        </TouchableOpacity>
        <TouchableOpacity style={gatherStyles.scanButton} onPress={onScan}>
          <Feather name="cpu" size={16} color={Paprika.surfaceLowest} />
          <Text style={gatherStyles.scanButtonText}>
            {imageUris.length > 1 ? t('photoImport.scanPages', { count: imageUris.length }) : t('photoImport.scanRecipe')}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

const gatherStyles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 16,
    paddingTop: 24,
  },
  strip: {
    gap: 12,
    paddingBottom: 8,
  },
  thumbWrap: {
    alignItems: 'center',
    gap: 6,
  },
  thumb: {
    width: 160,
    height: 220,
    borderRadius: PaprikaRadius.lg,
    backgroundColor: Paprika.surfaceHighest,
  },
  pageLabel: {
    ...PaprikaTypography.labelSmall,
    color: Paprika.textSecondary,
  },
  hint: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
    marginTop: 20,
    textAlign: 'center',
  },
  actions: {
    marginTop: 24,
    gap: 12,
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 12,
    borderRadius: PaprikaRadius.md,
    borderWidth: 1,
    borderColor: Paprika.primary,
  },
  addButtonText: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.primary,
  },
  scanButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 14,
    borderRadius: PaprikaRadius.md,
    backgroundColor: Paprika.primary,
  },
  scanButtonText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 16,
    color: Paprika.surfaceLowest,
  },
});

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: Paprika.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingTop: Platform.OS === 'web' ? 16 : 52,
    paddingBottom: 12,
    backgroundColor: Paprika.background,
    borderBottomWidth: 1,
    borderBottomColor: Paprika.surfaceHighest,
  },
  headerBack: {
    width: 36,
    alignItems: 'flex-start',
  },
  headerTitle: {
    ...PaprikaTypography.titleMedium,
    color: Paprika.textPrimary,
  },
  loadingContainer: {
    flex: 1,
    backgroundColor: Paprika.background,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
  },
  closeButton: {
    position: 'absolute',
    top: Platform.OS === 'web' ? 16 : 52,
    right: 16,
    padding: 8,
  },
  loadingText: {
    ...PaprikaTypography.titleMedium,
    color: Paprika.textPrimary,
    marginTop: 20,
    textAlign: 'center',
  },
  loadingHint: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
    marginTop: 8,
    textAlign: 'center',
  },
  errorIcon: {
    marginBottom: 16,
  },
  errorTitle: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 20,
    color: Paprika.textPrimary,
    marginBottom: 8,
    textAlign: 'center',
  },
  errorBody: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 24,
  },
  retryButton: {
    backgroundColor: Paprika.primary,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: PaprikaRadius.md,
  },
  retryButtonText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 15,
    color: Paprika.surfaceLowest,
  },
});
